import { useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DownloadOutlined,
  EditOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { downloadClaimFormDefinitionDocx } from "@accredo/fe-shared/services/claimFormService.js";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import ClaimFormPreviewModal from "./ClaimFormPreviewModal.jsx";
import ClaimFormDefinitionEditModal from "./ClaimFormDefinitionEditModal.jsx";

export default function ClaimFormOverviewTab({
  claimForm,
  canEditDefinition = false,
  onReload,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const currentDefinition = claimForm.current_definition;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadClaimFormDefinitionDocx(
        claimForm.id,
        currentDefinition.id,
        `${currentDefinition.docx_template}.docx`
      );
    } catch (error) {
      enqueueSnackbar("DOCX-Vorlage konnte nicht heruntergeladen werden", {
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Aktuelle Definition
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2">
            Datei: {currentDefinition?.definition_file_name ?? "–"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">
              DOCX-Vorlage: {currentDefinition?.docx_template ?? "–"}
            </Typography>
            {currentDefinition?.docx_template && (
              <Tooltip title="DOCX-Vorlage herunterladen">
                <IconButton size="small" onClick={handleDownload} disabled={isDownloading}>
                  <DownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2">
            DOCX-Pfad: {currentDefinition?.docx_template_path ?? "–"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="tertiary"
              size="small"
              startIcon={<VisibilityOutlined />}
              onClick={() => setIsPreviewOpen(true)}
            >
              Vorschau
            </Button>
            {canEditDefinition && (
              <Button
                variant="primary"
                size="small"
                startIcon={<EditOutlined />}
                onClick={() => setIsEditOpen(true)}
              >
                Definition bearbeiten
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Verwendete Section-Templates
        </Typography>
        <Paper variant="outlined">
          {claimForm.section_templates?.length > 0 ? (
            <List disablePadding>
              {claimForm.section_templates.map((template, i) => (
                <Box key={template.id}>
                  {i > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <Chip
                        size="small"
                        label={template.up_to_date ? "aktuell" : "veraltet"}
                        color={template.up_to_date ? "success" : "warning"}
                      />
                    }
                  >
                    <ListItemText primary={template.name} secondary={template.key} />
                  </ListItem>
                </Box>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Keine Section-Templates zugeordnet
            </Typography>
          )}
        </Paper>
      </Box>
      <ClaimFormPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        claimForm={claimForm}
      />
      <ClaimFormDefinitionEditModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        claimForm={claimForm}
        onSaved={onReload}
      />
    </Box>
  );
}
